import { useState, useMemo } from 'react';
import {
	loadStripe,
	type Stripe,
	type StripeElementsOptions,
	type StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { formatBookingCurrency } from '../../utils/bookingFormat';
import type { PaidBookingIntentResponse } from '../../types/publicBooking';
import BookingSummaryRow from './BookingSummaryRow';

export interface BookingAmounts {
	serviceTotal: number;
	deposit: number;
	supportFee: number;
	totalDue: number;
	balance: number;
}

/** Billing identity, taken from the client-info step rather than re-asked for. */
export interface BookingBillingDetails {
	name: string;
	email: string;
	phone: string;
}

interface BookingPaymentProps {
	publishableKey: string;
	/** Fee preview for the breakdown and for sizing the card form before an intent exists. */
	amounts: BookingAmounts;
	billingDetails: BookingBillingDetails;
	onBack: () => void;
	/**
	 * Called once the customer has ticked both consents and the card form has
	 * validated, to obtain the PaymentIntent for this booking. Expected to
	 * throw an Error whose message can be shown to the customer when the
	 * server refuses (slot gone, provider not onboarded, …).
	 */
	onPrepareIntent: () => Promise<PaidBookingIntentResponse>;
	/** The deposit went through (or is processing). The booking itself is finalized by the caller. */
	onPaid: (intent: PaidBookingIntentResponse) => void;
}

/** Stripe wants an integer amount in the smallest unit; the deposit is in dollars. */
const toCents = (amount: number): number => Math.round(amount * 100);

/** Bookings are charged in US dollars — matches the PaymentIntent the server creates. */
const CURRENCY = 'usd';

/** Bookings are US-only, so the country is a constant rather than a question. */
const BILLING_COUNTRY = 'US';

/**
 * Every field switched off here MUST be supplied in `confirmParams` below.
 * Stripe raises an integration error when you opt out of collecting a detail
 * and then do not pass it — which fails the charge outright, so the two lists
 * have to be kept in step.
 *
 * Name, email and phone are already collected at step 2; asking again would be
 * the same data typed twice. Country is hidden because there is only one. The
 * ZIP deliberately stays: US issuers check it (AVS), and dropping it costs both
 * authorisation rates and the evidence needed to defend a chargeback — which on
 * deposits this small can exceed the deposit itself.
 */
const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
	layout: 'tabs',
	fields: {
		billingDetails: {
			name: 'never',
			email: 'never',
			phone: 'never',
			address: { country: 'never', postalCode: 'auto' },
		},
	},
	// Card only. Apple Pay and Google Pay ride on the card payment method, so
	// the PaymentIntent cannot exclude them — they are turned off here instead.
	// Link is off for a second reason: it asks for an email inside the card
	// form, which would undo hiding the email field. Set any to 'auto' to
	// bring it back.
	wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
	// Tells the element which country's postal format to validate against, now
	// that the picker it would otherwise read is hidden.
	defaultValues: { billingDetails: { address: { country: BILLING_COUNTRY } } },
};

// `loadStripe` injects a script tag, so it must run once per key rather than on
// every render. Keyed because stage and production publish different keys.
const stripePromises = new Map<string, Promise<Stripe | null>>();

const getStripe = (publishableKey: string): Promise<Stripe | null> => {
	const cached = stripePromises.get(publishableKey);
	if (cached) return cached;
	const promise = loadStripe(publishableKey);
	stripePromises.set(publishableKey, promise);
	return promise;
};

/**
 * Status of an existing PaymentIntent.
 *
 * Used when the customer comes back from an off-site step such as 3-D Secure:
 * the deposit may already be paid, and the page has to find that out before
 * showing anything that invites a second attempt.
 */
export const retrievePaymentStatus = async (
	publishableKey: string,
	clientSecret: string
): Promise<string | null> => {
	const stripe = await getStripe(publishableKey);
	if (!stripe) return null;
	const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
	return paymentIntent?.status ?? null;
};

const APPEARANCE: StripeElementsOptions['appearance'] = {
	theme: 'flat',
	variables: {
		colorPrimary: '#8b5e3c',
		colorText: '#1a1a1a',
		colorDanger: '#c0392b',
		borderRadius: '10px',
		fontSizeBase: '15px',
	},
};

/**
 * Step 6 of the paid booking flow: the fee breakdown, Stripe's card form, the
 * consent checkboxes and the pay button.
 *
 * Card details are collected by Stripe's own PaymentElement rather than the
 * plain inputs the mockup shows. Raw card numbers must never touch our DOM —
 * routing them through our own fields would put this app in PCI DSS scope.
 *
 * The element is mounted in Stripe's deferred-intent mode: it is told the
 * amount and currency up front and the PaymentIntent is fetched only when the
 * customer presses Pay. That ordering is the point — the server records a
 * pending booking when it creates the intent, so creating it any earlier
 * (say, on entering this step) would leave a pending record behind every time
 * someone looked at the price and went back. Nothing is written until the
 * customer has consented and asked to pay.
 */
const BookingPayment = ({
	publishableKey,
	amounts,
	billingDetails,
	onBack,
	onPrepareIntent,
	onPaid,
}: BookingPaymentProps) => {
	const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey]);
	const amountInCents = toCents(amounts.totalDue);

	// Stripe refuses a zero amount, and the server would refuse the intent too.
	// Nothing to charge means the link is misconfigured, not that it is free.
	if (amountInCents < 1) {
		return (
			<>
				<div className="pb__error-banner">
					<span>⚠️</span>
					<span>
						No deposit amount is set for this booking. Please contact the salon to
						confirm your appointment.
					</span>
				</div>
				<div className="pb__actions">
					<button type="button" className="pb__btn pb__btn--secondary" onClick={onBack}>
						← Back
					</button>
				</div>
			</>
		);
	}

	return (
		<Elements
			stripe={stripePromise}
			options={{
				mode: 'payment',
				amount: amountInCents,
				currency: CURRENCY,
				appearance: APPEARANCE,
			}}
		>
			<PaymentPanel
				amounts={amounts}
				billingDetails={billingDetails}
				onBack={onBack}
				onPrepareIntent={onPrepareIntent}
				onPaid={onPaid}
			/>
		</Elements>
	);
};

const PaymentPanel = ({
	amounts,
	billingDetails,
	onBack,
	onPrepareIntent,
	onPaid,
}: {
	amounts: BookingAmounts;
	billingDetails: BookingBillingDetails;
	onBack: () => void;
	onPrepareIntent: () => Promise<PaidBookingIntentResponse>;
	onPaid: (intent: PaidBookingIntentResponse) => void;
}) => {
	const stripe = useStripe();
	const elements = useElements();

	const [agreedCancellation, setAgreedCancellation] = useState(false);
	const [agreedTerms, setAgreedTerms] = useState(false);
	const [showFeeInfo, setShowFeeInfo] = useState(false);
	const [isPaying, setIsPaying] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const canPay = agreedCancellation && agreedTerms && Boolean(stripe && elements) && !isPaying;

	const handlePay = async () => {
		if (!stripe || !elements) return;
		setIsPaying(true);
		setError(null);
		try {
			// 1) Validate the card form first. In deferred mode Stripe requires
			//    this before confirming, and it is also the cheap check: a
			//    typo in the card number should not cost a server round-trip
			//    that holds a pending booking.
			const { error: submitError } = await elements.submit();
			if (submitError) {
				setError(submitError.message || 'Please check your card details.');
				return;
			}

			// 2) Ask the server for the PaymentIntent. It re-prices and re-checks
			//    the slot, so this is where "that time has just been taken"
			//    surfaces — as a thrown Error with the server's message.
			let intent: PaidBookingIntentResponse;
			try {
				intent = await onPrepareIntent();
			} catch (prepareError) {
				setError(
					prepareError instanceof Error && prepareError.message
						? prepareError.message
						: 'We could not start your payment. Please try again.'
				);
				return;
			}

			// The element was sized from the fee preview; the intent carries the
			// server's figure. Keep them in step before confirming, or Stripe
			// rejects the confirmation as a mismatch.
			const chargedCents = toCents(intent.totalDueToday);
			if (chargedCents !== toCents(amounts.totalDue)) {
				elements.update({ amount: chargedCents });
			}

			// 3) Confirm. `if_required` keeps the customer on the page for plain
			//    cards and only redirects for methods that genuinely need it
			//    (3DS, wallets).
			//
			// `billing_details` carries the fields the element was told not to
			// collect — see PAYMENT_ELEMENT_OPTIONS. The ZIP is absent on
			// purpose: the element still collects it, and anything it collects
			// takes precedence over what is passed here, so naming it would
			// only risk overwriting the real one with nothing.
			const result = await stripe.confirmPayment({
				elements,
				clientSecret: intent.clientSecret,
				redirect: 'if_required',
				confirmParams: {
					return_url: window.location.href,
					payment_method_data: {
						billing_details: {
							name: billingDetails.name,
							email: billingDetails.email,
							phone: billingDetails.phone,
							address: { country: BILLING_COUNTRY },
						},
					},
				},
			});

			if (result.error) {
				setError(result.error.message || 'Your payment could not be completed.');
				return;
			}

			const status = result.paymentIntent?.status;
			if (status === 'succeeded' || status === 'processing') {
				onPaid(intent);
			} else {
				setError('Your payment was not completed. Please try again.');
			}
		} catch {
			setError('Something went wrong while processing your payment. Please try again.');
		} finally {
			setIsPaying(false);
		}
	};

	const payButton = (
		<button
			type="button"
			className="pb__btn pb__btn--success"
			onClick={handlePay}
			disabled={!canPay}
		>
			{isPaying ? 'Processing…' : `💳 Pay ${formatBookingCurrency(amounts.totalDue)}`}
		</button>
	);

	const backButton = (
		<button
			type="button"
			className="pb__btn pb__btn--secondary"
			onClick={onBack}
			disabled={isPaying}
		>
			← Back
		</button>
	);

	return (
		<>
			<div className="pb__actions pb__actions--top">
				{backButton}
				{payButton}
			</div>

			<div className="pb__alert">
				<span className="pb__alert-icon">💳</span>
				<div className="pb__alert-body">
					A deposit is required to secure your appointment. The remaining balance is due
					on the day of your service.
				</div>
			</div>

			{error && (
				<div className="pb__error-banner">
					<span>⚠️</span>
					<span>{error}</span>
				</div>
			)}

			{/* Itemised fee breakdown */}
			<div className="pb-paybreak">
				<div className="pb-paybreak__heading">Payment Breakdown</div>
				<div className="pb-paybreak__group-label">Customer Charges</div>

				<div className="pb__summary">
					<BookingSummaryRow
						label="Service Price"
						value={formatBookingCurrency(amounts.serviceTotal)}
					/>
					<BookingSummaryRow
						label="Required Deposit"
						value={formatBookingCurrency(amounts.deposit)}
					/>

					<span className="pb__summary-label pb-paybreak__fee-label">
						Support Fee
						<span className="pb-paybreak__nonrefundable">Non-Refundable</span>
						<button
							type="button"
							className="pb-paybreak__info-toggle"
							onClick={() => setShowFeeInfo(open => !open)}
							aria-expanded={showFeeInfo}
							title="Learn more about the Support Fee"
						>
							i
						</button>
					</span>
					<span className="pb__summary-value">
						{formatBookingCurrency(amounts.supportFee)}
					</span>

					{showFeeInfo && (
						<div className="pb-paybreak__info">
							<strong>ℹ️ About the Support Fee</strong>
							The Support Fee is a non-refundable platform fee that covers the cost of
							operating the Beauty Business Portal (BBP) booking system, securing your
							reservation, and processing any applicable payment transactions. This
							fee is assessed based on your total service price and is collected at
							the time of booking. It is not returned in the event of a cancellation
							or no-show.
						</div>
					)}
				</div>

				<div className="pb-paybreak__total">
					<div className="pb-paybreak__total-label">Total Due Today</div>
					<div className="pb-paybreak__total-value">
						{formatBookingCurrency(amounts.totalDue)}
					</div>
				</div>

				<div className="pb-paybreak__balance">
					<div className="pb-paybreak__balance-label">
						Remaining Balance Due at Appointment
					</div>
					<div className="pb-paybreak__balance-value">
						{formatBookingCurrency(amounts.balance)}
					</div>
				</div>
			</div>

			<div className="pb__divider" />
			<div className="pb__section-label">Card Details</div>

			<div className="pb-stripe">
				{!stripe || !elements ? (
					<div className="pb-stripe__loading">
						<span className="pb__spinner" />
						Loading secure payment form…
					</div>
				) : null}
				<PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
			</div>

			{/* Consent & agreement */}
			<div className="pb-consent">
				<div className="pb-consent__heading">📋 Consent &amp; Agreement</div>

				<label className="pb-consent__row">
					<input
						type="checkbox"
						checked={agreedCancellation}
						onChange={event => setAgreedCancellation(event.target.checked)}
					/>
					<span className="pb-consent__text">
						I understand and agree to the <strong>48-Hour Cancellation Policy</strong>.
						Cancellations made less than 48 hours before the appointment may result in
						forfeiture of the deposit. No-shows will be charged the full deposit amount.
					</span>
				</label>

				<label className="pb-consent__row">
					<input
						type="checkbox"
						checked={agreedTerms}
						onChange={event => setAgreedTerms(event.target.checked)}
					/>
					<span className="pb-consent__text">
						I agree to the <a href="#terms">Terms of Service</a> and{' '}
						<a href="#privacy">Privacy Policy</a>. I authorize the charge of the deposit
						amount listed above to the card provided.
					</span>
				</label>
			</div>

			<div className="pb__actions">
				{backButton}
				{payButton}
			</div>
		</>
	);
};

export default BookingPayment;
